import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Typography,
  Spinner,
  Alert,
  IconButton,
  Tooltip,
  Chip,
} from '@material-tailwind/react';
import {
  LinkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  TrashIcon,
  EyeIcon,
  CalendarIcon,
  EnvelopeIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

import { createShareLink, getShare, deactivateShare } from '../services/reportApi';
import { friendlyTimeFormatDt } from '../utils';

const SECTION_LABELS = {
  cover: 'Cover',
  tableOfContents: 'Contents',
  about: 'About',
  marketTrends: 'Market Trends',
  onMarketComps: 'Market Comps',
  recentlySoldComps: 'Sold Comps',
  summaryPricing: 'Summary & Pricing',
  strategy: 'Services',
  assumptions: 'Thank You',
};

// Order for displaying sections in chart
const SECTION_ORDER = [
  'cover',
  'tableOfContents',
  'about',
  'marketTrends',
  'onMarketComps',
  'recentlySoldComps',
  'summaryPricing',
  'strategy',
  'assumptions',
];

// Format active engagement time
function formatActiveTime(seconds) {
  if (!seconds || seconds === 0) return '0 sec';
  if (seconds < 60) return `${seconds} sec`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins} min ${secs} sec` : `${mins} min`;
}

// Get engagement level based on active time
function getEngagementLevel(seconds) {
  if (!seconds || seconds < 60) {
    return { label: 'Glanced', color: 'gray' };
  }
  if (seconds < 180) {
    return { label: 'Reviewed', color: 'blue' };
  }
  return { label: 'Deeply engaged', color: 'green' };
}

function ShareDialog({ open, onClose, reportId, reportTitle }) {
  const [share, setShare] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadShare = useCallback(async (isRefresh = false) => {
    if (!reportId) return;
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);
      const data = await getShare(reportId);
      setShare(data.share || null);
    } catch (err) {
      console.error('Failed to load share:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [reportId]);

  const handleRefresh = () => {
    loadShare(true);
  };

  useEffect(() => {
    if (open && reportId) {
      loadShare();
      setCopied(false);
    }
  }, [loadShare, open, reportId]);

  const handleCreateShare = async () => {
    try {
      setIsCreating(true);
      setError(null);
      await createShareLink(reportId);
      // Refresh to get full share data with analytics
      await loadShare();
    } catch (err) {
      console.error('Failed to create share link:', err);
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);
      setError(null);
      await deactivateShare(reportId);
      setShare(null);
    } catch (err) {
      console.error('Failed to deactivate share:', err);
      setError(err.message);
    } finally {
      setIsDeactivating(false);
    }
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // const formatDateTime = (dateString) => {
  //   if (!dateString) return '-';
  //   return new Date(dateString).toLocaleString('en-US', {
  //     month: 'short',
  //     day: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit',
  //   });
  // };

  // Process events for charts
  const chartData = useMemo(() => {
    if (!share?.analytics?.events) return { views: [], sections: [] };

    const events = share.analytics.events;

    // Views by date
    const viewsByDate = {};
    events
      .filter((e) => e.event === 'opened')
      .forEach((e) => {
        const date = new Date(e.timestamp).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
        viewsByDate[date] = (viewsByDate[date] || 0) + 1;
      });
    const views = Object.entries(viewsByDate).map(([date, count]) => ({
      date,
      count,
    }));

    // Section views - count events per section
    const sectionCounts = {};
    events
      .filter((e) => e.event === 'section_viewed' && e.sectionId)
      .forEach((e) => {
        sectionCounts[e.sectionId] = (sectionCounts[e.sectionId] || 0) + 1;
      });

    // Include ALL sections in order, with 0 count for unviewed ones
    const sections = SECTION_ORDER.map((sectionId) => ({
      section: SECTION_LABELS[sectionId] || sectionId,
      sectionId,
      count: sectionCounts[sectionId] || 0,
    }));

    return { views, sections };
  }, [share?.analytics?.events]);

  return (
    <Dialog open={open} handler={onClose} size="md">
      <DialogHeader className="flex flex-col items-start">
        <Typography variant="h5">Share Report</Typography>
        <Typography variant="small" color="gray" className="font-normal">
          {reportTitle}
        </Typography>
      </DialogHeader>

      <DialogBody>
        {error && (
          <Alert color="red" className="mb-4" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="h-8 w-8" />
          </div>
        ) : share ? (
          /* Existing share link */
          <div className="space-y-4">
            <div className="p-4 border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-gray-600" />
                  <Typography variant="h6" color="blue-gray">
                    Share Link
                  </Typography>
                </div>
                <Chip
                  size="sm"
                  value={share.isActive ? 'Active' : 'Inactive'}
                  color={share.isActive ? 'green' : 'gray'}
                />
              </div>

              {/* Share URL */}
              <div className="flex items-center gap-2 mb-3">
                <Input
                  value={share.shareUrl}
                  readOnly
                  className="flex-1"
                  containerProps={{ className: 'flex-1' }}
                />
                <Tooltip content="Preview (no tracking)">
                  <IconButton
                    variant="outlined"
                    onClick={() => window.open(`${share.shareUrl}?preview=true`, '_blank')}
                  >
                    <EyeIcon className="h-4 w-4" />
                  </IconButton>
                </Tooltip>
                <Tooltip content={copied ? 'Copied!' : 'Copy link for customer'}>
                  <IconButton
                    variant="outlined"
                    onClick={() => copyToClipboard(share.shareUrl)}
                  >
                    {copied ? (
                      <CheckIcon className="h-4 w-4 text-green-500" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    )}
                  </IconButton>
                </Tooltip>
              </div>

              {/* Share info */}
              <div className="text-sm text-gray-600 space-y-1">
                {share.customerEmail && (
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="h-4 w-4" />
                    <span>{share.customerEmail}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  <span>Created {formatDate(share.createdAt)}</span>
                  {share.expiresAt && (
                    <span className="text-gray-400">· Expires {formatDate(share.expiresAt)}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Analytics */}
            {share.analytics && (
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <Typography variant="h6" color="blue-gray">
                    Analytics
                  </Typography>
                  <Tooltip content="Refresh analytics">
                    <IconButton
                      variant="text"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                    >
                      <ArrowPathIcon className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </IconButton>
                  </Tooltip>
                </div>

                {/* Summary stats */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <EyeIcon className="h-5 w-5 text-gray-500" />
                    </div>
                    <Typography variant="h4" color="blue-gray">
                      {share.analytics.viewCount || 0}
                    </Typography>
                    <Typography variant="small" color="gray">
                      Views
                    </Typography>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <Typography variant="small" color="gray" className="mb-1">
                      Active Reading
                    </Typography>
                    <Typography variant="h6" color="blue-gray" className="font-semibold">
                      {share.analytics.viewCount > 0
                        ? formatActiveTime(share.analytics.totalActiveSeconds)
                        : '-'}
                    </Typography>
                    {share.analytics.viewCount > 0 && share.analytics.totalActiveSeconds > 0 && (
                      <Chip
                        size="sm"
                        value={getEngagementLevel(share.analytics.totalActiveSeconds).label}
                        color={getEngagementLevel(share.analytics.totalActiveSeconds).color}
                        className="mt-1"
                      />
                    )}
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <Typography variant="small" color="gray" className="mb-1">
                      First Viewed
                    </Typography>
                    <Typography variant="small" className="font-medium">
                      {share.analytics.firstViewedAt
                        ? friendlyTimeFormatDt(share.analytics.firstViewedAt)
                        : 'Not yet'}
                    </Typography>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <Typography variant="small" color="gray" className="mb-1">
                      Last Viewed
                    </Typography>
                    <Typography variant="small" className="font-medium">
                      {share.analytics.lastViewedAt
                        ? friendlyTimeFormatDt(share.analytics.lastViewedAt)
                        : 'Not yet'}
                    </Typography>
                  </div>
                </div>

                {/* Views by date chart */}
                {chartData.views.length > 0 && (
                  <div className="mb-4">
                    <Typography variant="small" color="gray" className="mb-2">
                      Views Over Time
                    </Typography>
                    <div className="h-32 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData.views}>
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
                          <RechartsTooltip />
                          <Bar dataKey="count" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Sections viewed chart - shows all sections */}
                <div>
                  <Typography variant="small" color="gray" className="mb-2">
                    Sections Viewed
                  </Typography>
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.sections} layout="vertical" margin={{ left: 10 }}>
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                        <YAxis
                          dataKey="section"
                          type="category"
                          tick={{ fontSize: 10 }}
                          width={100}
                          interval={0}
                        />
                        <RechartsTooltip />
                        <Bar dataKey="count" fill="#10B981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Deactivate button */}
            {share.isActive && (
              <div className="pt-2">
                <Button
                  variant="outlined"
                  color="red"
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={handleDeactivate}
                  disabled={isDeactivating}
                >
                  {isDeactivating ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <TrashIcon className="h-4 w-4" />
                  )}
                  Deactivate Link
                </Button>
                <Typography variant="small" color="gray" className="mt-1">
                  Deactivating will make the link unusable. You can create a new one after.
                </Typography>
              </div>
            )}
          </div>
        ) : (
          /* Create new share link */
          <div className="space-y-4">
            <Typography color="gray">
              Create a share link to send this report to your customer. They'll be able to view it in their browser.
            </Typography>
            <Button
              onClick={handleCreateShare}
              disabled={isCreating}
              className="flex items-center gap-2"
              fullWidth
            >
              {isCreating ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <LinkIcon className="h-4 w-4" />
              )}
              Generate Share Link
            </Button>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export default ShareDialog;
